import React, { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';

const VARIANTS = {
  primary:   'bg-ink text-cream hover:bg-ink-2 active:bg-ink-3 border border-ink',
  secondary: 'bg-surface text-text hover:bg-bg-2 active:bg-cream-3 border border-border',
  ghost:     'bg-transparent text-text hover:bg-bg-2 active:bg-cream-3 border border-transparent',
  link:      'bg-transparent text-text hover:underline underline-offset-4 px-0 border-0 shadow-none',
  danger:    'bg-danger text-cream hover:bg-danger/90 active:bg-danger/85 border border-danger',
  outline:   'bg-transparent text-text hover:bg-bg-2 border border-ink',
};

const SIZES = {
  xs: 'h-7  px-2   text-xs   gap-1.5 rounded-sm',
  sm: 'h-8  px-2.5 text-sm   gap-1.5 rounded-md',
  md: 'h-9  px-3.5 text-sm   gap-2   rounded-md',
  lg: 'h-11 px-5   text-base gap-2   rounded-lg',
};

export const Button = forwardRef(function Button(
  {
    as: Component = 'button',
    variant = 'primary',
    size = 'md',
    leftIcon,
    rightIcon,
    isLoading = false,
    isDisabled = false,
    fullWidth = false,
    className,
    children,
    type,
    ...rest
  },
  ref
) {
  const isButton = Component === 'button';
  return (
    <Component
      ref={ref}
      type={isButton ? type || 'button' : undefined}
      disabled={isButton ? (isDisabled || isLoading) : undefined}
      aria-disabled={isDisabled || isLoading}
      aria-busy={isLoading || undefined}
      className={cn(
        // base
        'inline-flex items-center justify-center font-semibold whitespace-nowrap select-none',
        'transition-colors duration-fast outline-none',
        'focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
        'disabled:opacity-50 disabled:pointer-events-none',
        // sizes
        SIZES[size],
        // variants
        VARIANTS[variant],
        // full width
        fullWidth && 'w-full',
        className
      )}
      {...rest}
    >
      {isLoading && <Loader2 className="animate-spin" style={{ width: '1em', height: '1em' }} aria-hidden />}
      {!isLoading && leftIcon}
      {children && <span>{children}</span>}
      {!isLoading && rightIcon}
    </Component>
  );
});

export default Button;
